import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, streamRepoExtract, streamRepoMessage } from "../lib/api";
import Markdown from "../components/Markdown";
import StreamProgress from "../components/StreamProgress";
import CitationChips from "../components/CitationChips";
import {
  REPO_SECURITY_TYPE_LABEL,
  repoDealLabel,
  repoHumanizeKey,
  type RepoBridgeFlag,
  type RepoCustomSection,
  type RepoDocKind,
  type RepoDocKindDef,
  type RepoMessage,
  type RepoMetadataValue,
  type RepoSchema,
  type RepoSchemaField,
  type RepoTranche,
  type RepositoryDeal,
  type RepositoryDoc,
} from "@docreview/shared";

// Natural sort so "01 Note Purchase Agreement" precedes "Exhibit A", and
// "Exhibit B" precedes "Exhibit C".
const byFilename = (a: { filename: string }, b: { filename: string }) =>
  a.filename.localeCompare(b.filename, undefined, { numeric: true });

/** Hide the trailing ```citations / ```json block during streaming (the
 * backend strips it authoritatively for the saved message). */
function stripCitationsFence(text: string): string {
  const i = text.search(/```(?:citations|json)\b/);
  return i >= 0 ? text.slice(0, i).trim() : text;
}


/**
 * Open a document in a new tab. PDFs open in the in-app viewer (which can
 * highlight a cited passage); other types download via a signed URL.
 */
function openRepoDocument(doc: { id: string; filename: string; mimeType: string }) {
  const isPdf = doc.mimeType === "application/pdf" || /\.pdf$/i.test(doc.filename);
  if (isPdf) {
    window.open(`/repository/pdf/${doc.id}`, "_blank", "noopener");
  } else {
    void api.getRepoDocUrl(doc.id).then(({ url }) => window.open(url, "_blank", "noopener"));
  }
}

export default function RepositoryDealPage() {
  // The URL segment is a friendly key — the deal LABEL (e.g. "CING", "AAPL2")
  // or, for old links, the Firestore doc id. The backend resolves either.
  // Everything downstream uses the resolved `deal.id`.
  const { dealKey } = useParams<{ dealKey: string }>();
  const [deal, setDeal] = useState<RepositoryDeal | null>(null);
  const [docs, setDocs] = useState<RepositoryDoc[]>([]);
  const [schema, setSchema] = useState<RepoSchema | null>(null);
  const [docKinds, setDocKinds] = useState<RepoDocKindDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [uncommitting, setUncommitting] = useState(false);

  const reload = useCallback(async () => {
    if (!dealKey) return;
    const { deal } = await api.getRepoDeal(dealKey);
    const { docs } = await api.listRepoDocs(deal.id);
    setDeal(deal);
    setDocs(docs);
  }, [dealKey]);

  const reloadDocKinds = useCallback(async () => {
    const { kinds } = await api.listRepoDocKinds();
    setDocKinds(kinds);
  }, []);

  const reloadSchema = useCallback(async () => {
    const { schema } = await api.getRepoSchema();
    setSchema(schema);
  }, []);

  useEffect(() => {
    if (!dealKey) return;
    (async () => {
      try {
        const [{ deal }, { schema }, { kinds }] = await Promise.all([
          api.getRepoDeal(dealKey),
          api.getRepoSchema(),
          api.listRepoDocKinds(),
        ]);
        // Docs are keyed by the resolved doc id, not the URL key.
        const { docs } = await api.listRepoDocs(deal.id);
        setDeal(deal);
        setSchema(schema);
        setDocKinds(kinds);
        setDocs(docs);
      } finally {
        setLoading(false);
      }
    })();
  }, [dealKey]);

  const amendmentKeys = useMemo(
    () => new Set(docKinds.filter((k) => k.isAmendment).map((k) => k.key)),
    [docKinds],
  );

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-6 text-slate-500 text-sm">Loading…</div>;
  if (!deal || !dealKey) return <div className="max-w-5xl mx-auto px-4 py-6 text-slate-500">Deal not found.</div>;
  const dealId = deal.id;

  const label = repoDealLabel(deal.ticker, deal.dealNumber);
  const committed = deal.status === "committed";

  return (
    <div className="max-w-screen-2xl mx-auto px-4 py-6">
      <div className="mb-4">
        <Link to="/repository" className="text-sm text-sky-600 hover:underline">
          ← Repository
        </Link>
      </div>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-mono font-semibold">{label}</h1>
          <div className="text-slate-600">
            {deal.companyName || (
              <span className="italic text-slate-400">Company name — fills in on extraction</span>
            )}
          </div>
          {deal.lenderEntity && (
            <div className="text-xs text-slate-400 mt-0.5">Lender: {deal.lenderEntity}</div>
          )}
        </div>
        <div className="text-right">
          <span
            className={`inline-block rounded-full text-xs font-medium px-2.5 py-1 capitalize ${
              committed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
            }`}
          >
            {deal.status.replace("_", " ")}
          </span>
          <div className="text-xs text-slate-400 mt-1">{deal.docCount} documents</div>
          {committed && (
            <button
              className="text-xs text-sky-600 hover:underline mt-1"
              disabled={uncommitting}
              title="Move this deal back to review so you can re-extract or edit it, then re-commit."
              onClick={async () => {
                setUncommitting(true);
                try {
                  const { deal: updated } = await api.uncommitRepoDeal(deal.id);
                  setDeal(updated);
                } finally {
                  setUncommitting(false);
                }
              }}
            >
              {uncommitting ? "Un-committing…" : "Un-commit to edit"}
            </button>
          )}
        </div>
      </div>

      {!committed && (
        <div className="card border-amber-200 bg-amber-50 p-4 mb-6 text-sm text-amber-800">
          This deal is still being prepared. Upload and tag its documents, review the
          metadata, then commit it to make it searchable in the repository.
        </div>
      )}

      {(deal.docsAddedAfterCommit?.length ?? 0) > 0 && (
        <div className="card border-amber-200 bg-amber-50 p-4 mb-6 text-sm text-amber-900">
          <div className="font-medium mb-1">
            {deal.docsAddedAfterCommit!.length} document
            {deal.docsAddedAfterCommit!.length === 1 ? " was" : "s were"} added after this deal was committed
          </div>
          The deal's metadata doesn't reflect{" "}
          {deal.docsAddedAfterCommit!.length === 1 ? "it" : "them"} yet:{" "}
          {deal.docsAddedAfterCommit!.map((d) => d.filename).join(", ")}.{" "}
          {committed
            ? "Un-commit, re-run extraction, review, and commit again."
            : "Re-run extraction and review before committing. Values that changed from what was reviewed come back unreviewed, with the old value shown."}
        </div>
      )}

      {(deal.bridgeFlags?.length ?? 0) > 0 && (
        <BridgeFlagsBanner
          dealId={deal.id}
          flags={deal.bridgeFlags!}
          onChange={(flags) => setDeal({ ...deal, bridgeFlags: flags })}
        />
      )}

      <div className="flex gap-6 items-start">
        {docs.length > 0 && (
          <aside className="hidden lg:block w-72 shrink-0 sticky top-4">
            <DocsSidebar docs={docs} docKinds={docKinds} />
          </aside>
        )}
        <div className="flex-1 min-w-0">
          {/* Committed deals lead with chat — the primary use. */}
          {committed && deal.docCount > 0 && <RepoChatPanel dealId={dealId} />}

          <DocumentsPanel
            dealId={dealId}
            docs={docs}
            onChange={reload}
            committed={committed}
            docKinds={docKinds}
            amendmentKeys={amendmentKeys}
            onDocKindsChange={reloadDocKinds}
          />

          {schema && (
            <MetadataPanel
              deal={deal}
              schema={schema}
              onSaved={(d) => setDeal(d)}
              onPromoted={async () => {
                await Promise.all([reload(), reloadSchema()]);
              }}
            />
          )}

          <CommitBar deal={deal} docs={docs} amendmentKeys={amendmentKeys} onCommitted={(d) => setDeal(d)} />

          {/* Pre-commit deals can still preview chat once docs are uploaded. */}
          {!committed && deal.docCount > 0 && (
            <div className="mt-8">
              <RepoChatPanel dealId={dealId} preview />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Sticky left rail of the deal's documents — click to open the PDF in a new
 * tab. Quick access while reading the metadata or chatting.
 */
function DocsSidebar({ docs, docKinds }: { docs: RepositoryDoc[]; docKinds: RepoDocKindDef[] }) {
  const labelOf = (kind: string) =>
    docKinds.find((k) => k.key === kind)?.label ?? repoHumanizeKey(kind);
  return (
    <div className="card p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
        Documents
      </h3>
      <ul className="space-y-1.5">
        {[...docs].sort(byFilename).map((d) => (
          <li key={d.id}>
            <button
              onClick={() => openRepoDocument(d)}
              title={labelOf(d.docKind)}
              className="w-full text-left text-sm text-sky-700 hover:underline break-words"
            >
              {d.filename}
            </button>
            <div className="text-[10px] text-slate-500">{labelOf(d.docKind)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ===========================================================================
// Documents
// ===========================================================================

function DocumentsPanel({
  dealId,
  docs,
  onChange,
  committed,
  docKinds,
  amendmentKeys,
  onDocKindsChange,
}: {
  dealId: string;
  docs: RepositoryDoc[];
  onChange: () => Promise<void>;
  committed: boolean;
  docKinds: RepoDocKindDef[];
  amendmentKeys: Set<string>;
  onDocKindsChange: () => Promise<void>;
}) {
  const [uploading, setUploading] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [manageTypesOpen, setManageTypesOpen] = useState(false);
  const [detecting, setDetecting] = useState(false);
  // Docs uploaded before auto-detection existed (or while Jev was down) are
  // still untagged "other"; offer to run detection over them.
  const undetected = docs.filter((d) => d.docKind === "other" && !d.docKindAuto).length;
  // Committed deals open read-only; uncommitted deals open in edit mode,
  // since tagging types / linking amendments IS the preparation workflow.
  // Committing / un-committing flips the mode with it — the panel stays
  // mounted across the status change, so the initializer alone isn't enough.
  const [editing, setEditing] = useState(!committed);
  useEffect(() => setEditing(!committed), [committed]);
  const fileRef = useRef<HTMLInputElement>(null);

  // Copy one date onto every document in the deal (deals usually share a
  // closing date). Amendments with their own dates can be adjusted after.
  const applyDateToAll = useCallback(
    async (ms: number | null) => {
      for (const d of docs) await api.patchRepoDoc(d.id, { docDate: ms });
      await onChange();
    },
    [docs, onChange],
  );

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      setError(null);
      setUploading(list.map((f) => f.name));
      // Sequential upload keeps the signed-URL flow simple and avoids
      // hammering the backend; deal doc sets are small.
      for (const file of list) {
        try {
          await api.uploadRepoDoc(dealId, file);
        } catch (e) {
          setError(`${file.name}: ${e instanceof Error ? e.message : String(e)}`);
        }
        setUploading((prev) => prev.filter((n) => n !== file.name));
      }
      setUploading([]);
      await onChange();
    },
    [dealId, onChange],
  );

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-semibold">Documents</h2>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            multiple
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(e) => {
              if (e.target.files) void uploadFiles(e.target.files);
              e.target.value = "";
            }}
          />
          {editing && undetected > 0 && (
            <button
              className="text-sm text-sky-600 hover:underline disabled:text-slate-400 disabled:no-underline"
              disabled={detecting}
              title="Read each untagged document's opening pages and set its type when confident"
              onClick={async () => {
                setDetecting(true);
                setError(null);
                try {
                  // The server handles a bounded batch per call; keep calling
                  // until it reports nothing left.
                  // Tried ids go back as `exclude`, so failures are skipped
                  // and every round moves on to new docs.
                  let failed = 0;
                  const tried: string[] = [];
                  for (let round = 0; round < 50; round++) {
                    const r = await api.detectRepoDocKinds(dealId, tried);
                    failed += r.failed;
                    tried.push(...r.attempted);
                    await onChange();
                    if (r.remaining <= 0 || r.attempted.length === 0) break;
                  }
                  if (failed > 0) {
                    setError(
                      `Couldn't detect the type of ${failed} document${failed === 1 ? "" : "s"} — try again, or tag ${failed === 1 ? "it" : "them"} by hand.`,
                    );
                  }
                } catch (e) {
                  setError(e instanceof Error ? e.message : String(e));
                } finally {
                  setDetecting(false);
                }
              }}
            >
              {detecting ? "Detecting types…" : `Detect types (${undetected})`}
            </button>
          )}
          {editing && (
            <button className="text-sm text-sky-600 hover:underline" onClick={() => setManageTypesOpen(true)}>
              Manage types
            </button>
          )}
          <button className="btn-secondary" onClick={() => fileRef.current?.click()}>
            Add files
          </button>
          <button className="btn-secondary" onClick={() => setEditing((v) => !v)}>
            {editing ? "Done" : "Edit"}
          </button>
        </div>
      </div>
      {manageTypesOpen && (
        <TypeManagerModal
          docKinds={docKinds}
          onClose={() => setManageTypesOpen(false)}
          onChanged={async () => {
            // Reload kinds AND docs: an amendment-flag flip re-stamps derived
            // state (amendmentCount, per-doc amendment rendering) server-side.
            await onDocKindsChange();
            await onChange();
          }}
        />
      )}

      <div
        className="card p-3"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (e.dataTransfer.files) void uploadFiles(e.dataTransfer.files);
        }}
      >
        {docs.length === 0 && uploading.length === 0 ? (
          <div className="text-center text-sm text-slate-400 py-6">
            Drag a deal's documents here, or click “Add files”. .pdf and .docx supported.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500">
                <th className="font-medium pb-2">Document</th>
                <th className="font-medium pb-2 w-48">Type</th>
                <th className="font-medium pb-2 w-56">Amends</th>
                <th className="font-medium pb-2 w-32">Date</th>
                {editing && <th className="pb-2 w-16" />}
              </tr>
            </thead>
            <tbody>
              {[...docs].sort(byFilename).map((doc) =>
                editing ? (
                  <DocRow
                    key={doc.id}
                    doc={doc}
                    siblings={[...docs].sort(byFilename)}
                    onChange={onChange}
                    docKinds={docKinds}
                    amendmentKeys={amendmentKeys}
                    onDocKindsChange={onDocKindsChange}
                    onApplyDateToAll={applyDateToAll}
                    onManageTypes={() => setManageTypesOpen(true)}
                  />
                ) : (
                  <DocViewRow
                    key={doc.id}
                    doc={doc}
                    siblings={docs}
                    docKinds={docKinds}
                    amendmentKeys={amendmentKeys}
                  />
                ),
              )}
              {uploading.map((name) => (
                <tr key={name} className="text-slate-400">
                  <td className="py-1.5">{name}</td>
                  <td colSpan={editing ? 4 : 3} className="py-1.5 text-xs italic">
                    uploading…
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
      {editing && (
        <p className="text-xs text-slate-500 mt-2">
          Tag each document's type. Amendments and forbearances must be linked to the
          document they amend.
        </p>
      )}
    </section>
  );
}

/**
 * What the K: drive bridge noticed but deliberately didn't act on: a signed
 * PDF it imported has since changed or disappeared on K:. The bridge never
 * edits or deletes anything here, so an admin decides — delete the doc (the
 * bridge then imports the current K: version on its next run), or dismiss.
 */
function BridgeFlagsBanner({
  dealId,
  flags,
  onChange,
}: {
  dealId: string;
  flags: RepoBridgeFlag[];
  onChange: (flags: RepoBridgeFlag[]) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  return (
    <div className="card border-amber-200 bg-amber-50 p-4 mb-6 text-sm text-amber-900">
      <div className="font-medium mb-1">K: drive changes to review</div>
      <ul className="space-y-1.5">
        {flags.map((f) => (
          <li key={f.key} className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <span className="font-medium">{f.filename}</span>{" "}
              {f.issue === "changed"
                ? "was changed on K: after it was imported. Delete it here and tonight's run imports the current version, or dismiss to keep this one."
                : "is no longer on K: (moved, renamed or deleted). Delete it here if it shouldn't be part of the deal, or dismiss."}
              <div className="text-xs text-amber-700 break-all">K:\…\{f.sourcePath.replace(/\//g, "\\")}</div>
            </div>
            <button
              className="text-xs text-sky-700 hover:underline shrink-0 disabled:text-slate-400"
              disabled={busy !== null}
              onClick={async () => {
                setBusy(f.key);
                try {
                  const { flags: next } = await api.ackRepoBridgeFlag(dealId, f.key);
                  onChange(next);
                } finally {
                  setBusy(null);
                }
              }}
            >
              {busy === f.key ? "Dismissing…" : "Dismiss"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Provenance for an auto-detected type: a quiet "auto-detected" note while
 * the type is still Jev's, or — when Jev wasn't confident and the doc is
 * still "other" — its guess as a suggestion (one click to accept in edit
 * mode). Renders nothing once someone has picked a different type.
 */
function DocKindAutoNote({
  doc,
  labelOf,
  kindExists,
  isAmendmentKind,
  siblings = [],
  onAccept,
}: {
  doc: RepositoryDoc;
  labelOf: (kind: string) => string;
  kindExists?: (kind: string) => boolean;
  isAmendmentKind?: (kind: string) => boolean;
  siblings?: RepositoryDoc[];
  // parentDocId is set (and required) when the suggested kind is an
  // amendment, so the kind and its parent link land in one PATCH.
  onAccept?: (kind: string, parentDocId?: string) => void;
}) {
  const [pickingParent, setPickingParent] = useState(false);
  const auto = doc.docKindAuto;
  if (!auto) return null;
  const pct = `${Math.round(auto.confidence * 100)}%`;
  if (auto.applied && doc.docKind === auto.kind) {
    return (
      <div className="text-[10px] text-slate-400" title={`Detected by ${auto.model}`}>
        auto-detected · {pct}
      </div>
    );
  }
  if (!auto.applied && doc.docKind === "other" && auto.kind !== "other") {
    return (
      <div className="text-[10px] text-amber-700">
        Suggested: {labelOf(auto.kind)} ({pct})
        {onAccept && (kindExists?.(auto.kind) ?? true) && !pickingParent && (
          <button
            className="ml-1 text-sky-600 hover:underline"
            onClick={() =>
              isAmendmentKind?.(auto.kind) ? setPickingParent(true) : onAccept(auto.kind)
            }
          >
            use
          </button>
        )}
        {onAccept && pickingParent && (
          // An amendment must say what it amends — accept only with a parent.
          <select
            className="input py-0.5 mt-1 text-[11px]"
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) onAccept(auto.kind, e.target.value);
            }}
            onBlur={() => setPickingParent(false)}
            autoFocus
          >
            <option value="">— which document does it amend? —</option>
            {siblings.map((s) => (
              <option key={s.id} value={s.id}>
                {s.filename}
              </option>
            ))}
          </select>
        )}
      </div>
    );
  }
  return null;
}

function formatPriorValue(v: string | number | boolean | null, field: RepoSchemaField): string {
  if (v === null || v === "") return "(empty)";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number" && field.type === "date") {
    return new Date(v).toLocaleDateString(undefined, { timeZone: "UTC" });
  }
  if (typeof v === "number" && field.type === "usd") return `$${v.toLocaleString()}`;
  return `${v}${field.unit && typeof v === "number" ? ` ${field.unit}` : ""}`;
}

/** Read-only document row — plain text instead of input chrome. */
function DocViewRow({
  doc,
  siblings,
  docKinds,
  amendmentKeys,
}: {
  doc: RepositoryDoc;
  siblings: RepositoryDoc[];
  docKinds: RepoDocKindDef[];
  amendmentKeys: Set<string>;
}) {
  const labelOf = (kind: string) =>
    docKinds.find((k) => k.key === kind)?.label ?? repoHumanizeKey(kind);
  const isAmendment = amendmentKeys.has(doc.docKind);
  const parent = doc.parentDocId ? siblings.find((s) => s.id === doc.parentDocId) : undefined;
  const needsParent = isAmendment && !doc.parentDocId;
  return (
    <tr className={`border-t border-slate-100 ${needsParent ? "bg-red-50" : ""}`}>
      <td className="py-2 pr-3">
        <button
          onClick={() => openRepoDocument(doc)}
          className="text-left hover:text-sky-600 hover:underline break-words"
        >
          {doc.filename}
        </button>
        {doc.source && (
          <div className="text-[10px] text-slate-400" title={doc.source.path}>
            from K: drive
          </div>
        )}
      </td>
      <td className="py-2 pr-3">
        {labelOf(doc.docKind)}
        <DocKindAutoNote doc={doc} labelOf={labelOf} />
      </td>
      <td className="py-2 pr-3">
        {isAmendment ? (
          parent ? (
            <span className="break-words">{parent.filename}</span>
          ) : (
            <span className="text-xs text-red-600">unlinked — Edit to link</span>
          )
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td className="py-2 whitespace-nowrap">
        {doc.docDate ? (
          new Date(doc.docDate).toLocaleDateString(undefined, { timeZone: "UTC" })
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
    </tr>
  );
}

function DocRow({
  doc,
  siblings,
  onChange,
  docKinds,
  amendmentKeys,
  onDocKindsChange,
  onApplyDateToAll,
  onManageTypes,
}: {
  doc: RepositoryDoc;
  siblings: RepositoryDoc[];
  onChange: () => Promise<void>;
  docKinds: RepoDocKindDef[];
  amendmentKeys: Set<string>;
  onDocKindsChange: () => Promise<void>;
  onApplyDateToAll: (ms: number | null) => Promise<void>;
  onManageTypes: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const isAmendment = amendmentKeys.has(doc.docKind);
  const needsParent = isAmendment && !doc.parentDocId;

  // Ensure the current value is always selectable, even if it's a custom key
  // not (yet) in the loaded catalog.
  const baseKinds = docKinds.some((k) => k.key === doc.docKind)
    ? docKinds
    : [{ key: doc.docKind, label: repoHumanizeKey(doc.docKind), order: -1 }, ...docKinds];
  // Alphabetical, with the catch-all "Other" pinned to the bottom.
  const kindOptions = [...baseKinds].sort((a, b) => {
    if (a.key === "other") return 1;
    if (b.key === "other") return -1;
    return a.label.localeCompare(b.label);
  });

  const patch = async (
    body: Partial<{ docKind: RepoDocKind; parentDocId: string | null; docDate: number | null }>,
  ) => {
    setBusy(true);
    try {
      await api.patchRepoDoc(doc.id, body);
      await onChange();
    } finally {
      setBusy(false);
    }
  };

  const addType = async () => {
    const label = window.prompt("New document type (e.g. “Transfer Agent Letter”):")?.trim();
    if (!label) return;
    // New types are normal documents. The rare amendment/forbearance case (which
    // requires a parent link) is set explicitly via "Manage types…", so a
    // misclick here can't silently turn an ordinary type into an amendment.
    setBusy(true);
    try {
      const { kind } = await api.addRepoDocKind({ label });
      await onDocKindsChange();
      await api.patchRepoDoc(doc.id, { docKind: kind.key });
      await onChange();
    } finally {
      setBusy(false);
    }
  };

  const openDoc = () => openRepoDocument(doc);

  // Date is edited locally and only committed on blur. Committing on every
  // keystroke (the native date input fires a change as soon as a digit makes
  // a valid date) plus the reload-after-save would reset the field mid-entry —
  // typing "2024" into the year would commit "0002" and snap back.
  const dateValue = doc.docDate ? new Date(doc.docDate).toISOString().slice(0, 10) : "";
  const [dateStr, setDateStr] = useState(dateValue);
  useEffect(() => setDateStr(dateValue), [dateValue]);
  const commitDate = () => {
    if (dateStr === dateValue) return;
    void patch({ docDate: dateStr ? new Date(dateStr).getTime() : null });
  };

  return (
    <tr className={`border-t border-slate-100 ${needsParent ? "bg-red-50" : ""}`}>
      <td className="py-1.5 pr-2">
        <button onClick={openDoc} className="text-left hover:text-sky-600 hover:underline">
          {doc.filename}
        </button>
      </td>
      <td className="py-1.5 pr-2">
        <DocKindSelect
          value={doc.docKind}
          options={kindOptions}
          label={kindOptions.find((k) => k.key === doc.docKind)?.label ?? repoHumanizeKey(doc.docKind)}
          disabled={busy}
          onSelect={(key) => patch({ docKind: key })}
          onAdd={addType}
          onManage={onManageTypes}
        />
        <DocKindAutoNote
          doc={doc}
          labelOf={(k) => kindOptions.find((o) => o.key === k)?.label ?? repoHumanizeKey(k)}
          kindExists={(k) => docKinds.some((o) => o.key === k)}
          isAmendmentKind={(k) => amendmentKeys.has(k)}
          siblings={siblings.filter((s) => s.id !== doc.id)}
          onAccept={busy ? undefined : (k, parentDocId) => void patch({ docKind: k, ...(parentDocId ? { parentDocId } : {}) })}
        />
      </td>
      <td className="py-1.5 pr-2">
        {isAmendment ? (
          <select
            className={`input py-1 ${needsParent ? "border-red-400" : ""}`}
            value={doc.parentDocId ?? ""}
            disabled={busy}
            onChange={(e) => patch({ parentDocId: e.target.value || null })}
          >
            <option value="">— select document —</option>
            {siblings
              .filter((s) => s.id !== doc.id)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.filename}
                </option>
              ))}
          </select>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td className="py-1.5 pr-2">
        <div className="flex items-center gap-1">
          <input
            type="date"
            className="input py-1"
            value={dateStr}
            disabled={busy}
            onChange={(e) => setDateStr(e.target.value)}
            onBlur={commitDate}
          />
          <button
            type="button"
            className="text-[11px] text-sky-600 hover:underline whitespace-nowrap disabled:opacity-40 disabled:no-underline"
            disabled={busy || !dateStr}
            title="Apply this date to every document in this deal"
            onClick={() => onApplyDateToAll(dateStr ? new Date(dateStr).getTime() : null)}
          >
            ↧ all
          </button>
        </div>
      </td>
      <td className="py-1.5 text-right">
        <button
          className="text-xs text-red-600 hover:underline"
          disabled={busy}
          onClick={async () => {
            if (!confirm(`Remove ${doc.filename}?`)) return;
            setBusy(true);
            try {
              await api.deleteRepoDoc(doc.id);
              await onChange();
            } catch (e) {
              alert(e instanceof Error ? e.message : String(e));
              setBusy(false);
            }
          }}
        >
          Remove
        </button>
      </td>
    </tr>
  );
}

// ===========================================================================
// Metadata
// ===========================================================================

/** Stringify a stored metadata value for an <input>. */
function toInputValue(v: RepoMetadataValue | undefined, field: RepoSchemaField): string {
  const val = v?.value;
  if (val == null) return "";
  if (field.type === "date" && typeof val === "number") {
    return new Date(val).toISOString().slice(0, 10);
  }
  if (field.type === "boolean") return val === true ? "true" : val === false ? "false" : "";
  return String(val);
}

/** Coerce an input string back to the field's stored value type. */
function fromInputValue(raw: string, field: RepoSchemaField): string | number | boolean | null {
  if (raw === "") return null;
  switch (field.type) {
    case "usd": {
      const n = Number(raw.replace(/[^0-9.\-]/g, ""));
      return Number.isFinite(n) ? Math.round(n) : null;
    }
    case "number": {
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }
    case "date": {
      const t = new Date(raw).getTime();
      return Number.isFinite(t) ? t : null;
    }
    case "boolean":
      return raw === "true";
    default:
      return raw;
  }
}

function MetadataPanel({
  deal,
  schema,
  onSaved,
  onPromoted,
}: {
  deal: RepositoryDeal;
  schema: RepoSchema;
  onSaved: (d: RepositoryDeal) => void;
  onPromoted: () => Promise<void>;
}) {
  // Local draft keyed by field key -> raw input string.
  const initialDraft = useMemo(() => {
    const d: Record<string, string> = {};
    for (const f of schema.fields) d[f.key] = toInputValue(deal.metadata?.[f.key], f);
    return d;
  }, [deal.metadata, schema.fields]);

  const [draft, setDraft] = useState<Record<string, string>>(initialDraft);
  const [tranches, setTranches] = useState<RepoTranche[]>(deal.tranches ?? []);
  const [sections, setSections] = useState<RepoCustomSection[]>(deal.customSections ?? []);
  // The panel opens as a read-only summary (the everyday "look at the deal"
  // view); the input-heavy editor only appears behind the Edit button.
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractStatus, setExtractStatus] = useState<string | null>(null);
  const [extractError, setExtractError] = useState<string | null>(null);

  const extract = async () => {
    setExtracting(true);
    setExtractError(null);
    setExtractStatus("Starting…");
    try {
      await streamRepoExtract(deal.id, {
        onStatus: (t) => setExtractStatus(t),
        onDone: (updated) => onSaved(updated),
        onError: (msg) => setExtractError(msg),
      });
    } catch (e) {
      setExtractError(e instanceof Error ? e.message : String(e));
    } finally {
      setExtracting(false);
      setExtractStatus(null);
    }
  };

  useEffect(() => setDraft(initialDraft), [initialDraft]);
  useEffect(() => setTranches(deal.tranches ?? []), [deal.tranches]);
  useEffect(() => setSections(deal.customSections ?? []), [deal.customSections]);

  const fieldsByGroup = useMemo(() => {
    const groups: Record<string, RepoSchemaField[]> = { core: [], prose: [], custom: [] };
    for (const f of schema.fields) (groups[f.group] ??= []).push(f);
    return groups;
  }, [schema.fields]);

  const dirty = useMemo(() => {
    const changed: Record<string, Partial<RepoMetadataValue>> = {};
    for (const f of schema.fields) {
      if (draft[f.key] !== initialDraft[f.key]) {
        changed[f.key] = { value: fromInputValue(draft[f.key] ?? "", f) };
      }
    }
    return changed;
  }, [draft, initialDraft, schema.fields]);

  const tranchesDirty = JSON.stringify(tranches) !== JSON.stringify(deal.tranches ?? []);
  const sectionsDirty = JSON.stringify(sections) !== JSON.stringify(deal.customSections ?? []);
  const hasChanges = Object.keys(dirty).length > 0 || tranchesDirty || sectionsDirty;

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      const { deal: updated } = await api.patchRepoDealMetadata(deal.id, {
        ...(Object.keys(dirty).length > 0 ? { metadata: dirty } : {}),
        ...(tranchesDirty ? { tranches } : {}),
        ...(sectionsDirty ? { customSections: sections } : {}),
      });
      onSaved(updated);
      setSaved(true);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const cancelEdit = () => {
    if (hasChanges && !confirm("Discard unsaved changes?")) return;
    setDraft(initialDraft);
    setTranches(deal.tranches ?? []);
    setSections(deal.customSections ?? []);
    setEditing(false);
  };

  const promote = async (label: string) => {
    if (!label.trim()) return;
    if (
      !confirm(
        `Promote “${label}” to a filterable field?\n\nThis adds it to the metadata schema and moves the matching section on every deal into that field, so you can filter on it across the repository.`,
      )
    )
      return;
    setBusy(true);
    try {
      const { migrated } = await api.promoteRepoSection(label);
      await onPromoted();
      alert(`Promoted “${label}” to a filterable field (updated ${migrated} deal${migrated === 1 ? "" : "s"}).`);
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="font-semibold">Metadata</h2>
          {deal.extractedAt && (
            <div className="text-[11px] text-slate-500">
              Extracted with {deal.extractionModel ?? "Claude"} ·{" "}
              {new Date(deal.extractedAt).toLocaleString()}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {saved && !hasChanges && <span className="text-xs text-emerald-600">Saved</span>}
          {editing ? (
            <>
              <button className="btn-secondary" disabled={busy} onClick={cancelEdit}>
                Cancel
              </button>
              <button className="btn-primary" disabled={!hasChanges || busy} onClick={save}>
                {busy ? "Saving…" : "Save metadata"}
              </button>
            </>
          ) : (
            <>
              <button
                className="btn-secondary"
                disabled={extracting || deal.docCount === 0 || deal.status === "committed"}
                onClick={extract}
                title={
                  deal.status === "committed"
                    ? "This deal is committed. Use “Un-commit to edit” (top right) to re-extract."
                    : deal.docCount === 0
                      ? "Add documents first"
                      : "Have Claude read the documents and propose values (preserves anything you've already confirmed)"
                }
              >
                {extracting ? "Extracting…" : deal.extractedAt ? "Re-extract" : "Extract with Claude"}
              </button>
              <button
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={extracting}
                title={
                  extracting
                    ? "Extraction is running — edits made now would be overwritten when it finishes."
                    : undefined
                }
                onClick={() => setEditing(true)}
              >
                Edit
              </button>
            </>
          )}
        </div>
      </div>
      {extractError && <div className="text-sm text-red-600 mb-2">{extractError}</div>}
      {extracting && (
        <div className="text-xs text-slate-500 mb-2">
          {extractStatus ?? "Working…"} (this can take a minute or two for a large deal)
        </div>
      )}

      <div className="card p-4">
        {editing ? (
          <>
            <p className="text-xs text-slate-500 mb-4">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 mr-1.5 align-middle" />
              Extracted by Claude and not yet reviewed by a person — saving an edit to a
              field marks it reviewed.
            </p>
            <TranchesEditor tranches={tranches} onChange={setTranches} />

            <FieldGroup
              title="Key facts"
              fields={fieldsByGroup.core ?? []}
              deal={deal}
              draft={draft}
              setDraft={setDraft}
            />
            <FieldGroup
              title="Summary"
              fields={fieldsByGroup.prose ?? []}
              deal={deal}
              draft={draft}
              setDraft={setDraft}
            />
            {(fieldsByGroup.custom ?? []).length > 0 && (
              <FieldGroup
                title="Custom fields"
                fields={fieldsByGroup.custom ?? []}
                deal={deal}
                draft={draft}
                setDraft={setDraft}
              />
            )}

            <CustomSectionsEditor sections={sections} onChange={setSections} onPromote={promote} />
          </>
        ) : (
          <MetadataDisplay deal={deal} schema={schema} fieldsByGroup={fieldsByGroup} />
        )}
      </div>
    </section>
  );
}

/** Format a stored value for the read-only display, per the field's type. */
function formatFieldValue(field: RepoSchemaField, stored: RepoMetadataValue | undefined): string | null {
  const val = stored?.value;
  if (val == null || val === "") return null;
  switch (field.type) {
    case "usd":
      return typeof val === "number" ? `$${Math.round(val).toLocaleString()}` : String(val);
    case "number":
      if (field.unit === "%") return `${val}%`;
      return field.unit ? `${val} ${field.unit}` : String(val);
    case "date":
      // Date-only values are stored at UTC midnight — format in UTC so users
      // west of UTC don't see the previous calendar day (matches the library).
      return typeof val === "number"
        ? new Date(val).toLocaleDateString(undefined, { timeZone: "UTC" })
        : String(val);
    case "boolean":
      return val === true ? "Yes" : "No";
    case "enum":
      // Known machine values get their canonical labels ("ab_notes" →
      // "A/B Notes"); the humanizer is only a fallback for later additions.
      return REPO_SECURITY_TYPE_LABEL[String(val)] ?? repoHumanizeKey(String(val));
    default:
      return String(val);
  }
}

const fmtUsd = (n: number | undefined) =>
  n != null ? `$${Math.round(n).toLocaleString()}` : "—";

/**
 * The read-only "finished" presentation of a deal's metadata — clean
 * typography instead of input chrome. Source quotes move into hover tooltips;
 * the amber unreviewed dot is kept. Editing happens behind the Edit button.
 */
function MetadataDisplay({
  deal,
  schema,
  fieldsByGroup,
}: {
  deal: RepositoryDeal;
  schema: RepoSchema;
  fieldsByGroup: Record<string, RepoSchemaField[]>;
}) {
  const tranches = deal.tranches ?? [];
  const sections = deal.customSections ?? [];
  const hasAnyValue =
    tranches.length > 0 ||
    sections.length > 0 ||
    schema.fields.some((f) => {
      const v = deal.metadata?.[f.key]?.value;
      return v != null && v !== "";
    });

  if (!hasAnyValue) {
    return (
      <p className="text-sm text-slate-400 py-2 text-center">
        No metadata yet — use “Extract with Claude” to fill these fields from the
        documents, or Edit to enter values by hand.
      </p>
    );
  }

  return (
    <>
      <TranchesView tranches={tranches} />
      <FieldGroupView title="Key facts" fields={fieldsByGroup.core ?? []} deal={deal} />
      <FieldGroupView title="Summary" fields={fieldsByGroup.prose ?? []} deal={deal} />
      <FieldGroupView title="Custom fields" fields={fieldsByGroup.custom ?? []} deal={deal} />
      <CustomSectionsView sections={sections} />
    </>
  );
}

function TranchesView({ tranches }: { tranches: RepoTranche[] }) {
  if (tranches.length === 0) return null;
  return (
    <div className="mb-5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
        Notes / Tranches
      </h3>
      {/* Seven columns don't fit a phone screen — scroll the table, not the page. */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              <th className="font-medium text-left pb-1">Label</th>
              <th className="font-medium text-right pb-1">Face value</th>
              <th className="font-medium text-right pb-1">Cash funded</th>
              <th className="font-medium text-right pb-1">OID</th>
              <th className="font-medium text-right pb-1">Rate</th>
              <th className="font-medium text-right pb-1">Term</th>
              <th className="font-medium text-right pb-1">Conv.</th>
            </tr>
          </thead>
          <tbody>
            {tranches.map((t) => (
              <tr key={t.id} className="border-t border-slate-100">
                <td className="py-1.5 pr-2 font-medium whitespace-nowrap">{t.label || "—"}</td>
                <td className="py-1.5 pl-2 text-right tabular-nums">{fmtUsd(t.faceValueUsd)}</td>
                <td className="py-1.5 pl-2 text-right tabular-nums">{fmtUsd(t.purchasePriceUsd)}</td>
                <td className="py-1.5 pl-2 text-right tabular-nums">{fmtUsd(t.oidUsd)}</td>
                <td className="py-1.5 pl-2 text-right tabular-nums">
                  {t.interestRatePct != null ? `${t.interestRatePct}%` : "—"}
                </td>
                <td className="py-1.5 pl-2 text-right tabular-nums">
                  {t.maturityMonths != null ? `${t.maturityMonths} mo` : "—"}
                </td>
                <td className="py-1.5 pl-2 text-right">{t.convertible ? "✓" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FieldGroupView({
  title,
  fields,
  deal,
}: {
  title: string;
  fields: RepoSchemaField[];
  deal: RepositoryDeal;
}) {
  // Empty key facts stay visible as "—" (a gap in the metrics is signal);
  // empty prose paragraphs are just noise in the finished view, so hide them.
  const visible = fields.filter((f) => {
    if (f.group !== "prose") return true;
    const v = deal.metadata?.[f.key]?.value;
    return v != null && v !== "";
  });
  if (visible.length === 0) return null;
  return (
    <div className="mt-5 first:mt-0">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
        {title}
      </h3>
      <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {visible.map((f) => (
          <FieldDisplay key={f.key} field={f} stored={deal.metadata?.[f.key]} />
        ))}
      </div>
    </div>
  );
}

function FieldDisplay({
  field,
  stored,
}: {
  field: RepoSchemaField;
  stored: RepoMetadataValue | undefined;
}) {
  const isProse = field.group === "prose";
  const display = formatFieldValue(field, stored);
  return (
    <div
      className={isProse ? "col-span-full" : ""}
      title={stored?.sourceQuote ? `“${stored.sourceQuote}”` : undefined}
    >
      <div
        className="text-xs font-medium uppercase tracking-wide text-slate-500"
        title={field.description}
      >
        {field.label}
      </div>
      {isProse ? (
        <p className="text-sm mt-0.5 leading-relaxed whitespace-pre-wrap">
          {display ?? <span className="text-slate-300">—</span>}
        </p>
      ) : (
        <div className="text-sm font-medium mt-0.5">
          {display ?? <span className="text-slate-300">—</span>}
        </div>
      )}
    </div>
  );
}

function CustomSectionsView({ sections }: { sections: RepoCustomSection[] }) {
  if (sections.length === 0) return null;
  return (
    <div className="mt-5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
        Deal-specific terms
      </h3>
      <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
        {sections.map((s) => (
          <div key={s.id} title={s.sourceQuote ? `“${s.sourceQuote}”` : undefined}>
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              {s.label || "Untitled"}
            </div>
            <p className="text-sm mt-0.5 leading-relaxed whitespace-pre-wrap">
              {s.value || <span className="text-slate-300">—</span>}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Editor for Claude-generated, deal-specific sections (the long tail). Each is
 * label + free-text value, with a source quote when extracted. Sections can be
 * added, edited, removed, or promoted to a filterable catalog field.
 */
function CustomSectionsEditor({
  sections,
  onChange,
  onPromote,
}: {
  sections: RepoCustomSection[];
  onChange: (s: RepoCustomSection[]) => void;
  onPromote: (label: string) => void;
}) {
  const update = (i: number, patch: Partial<RepoCustomSection>) =>
    onChange(sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Deal-specific terms
        </h3>
        <button
          className="text-xs text-sky-600 hover:underline"
          onClick={() =>
            onChange([...sections, { id: `cs_new_${sections.length}_${Date.now()}`, label: "", value: "" }])
          }
        >
          + Add section
        </button>
      </div>
      {sections.length === 0 ? (
        <p className="text-xs text-slate-400">
          Claude adds notable terms that don't fit the fields above (e.g. Put Pricing, Collateral
          Release). None yet — re-extract, or add one.
        </p>
      ) : (
        <div className="space-y-3">
          {sections.map((s, i) => (
            <div key={s.id} className="rounded-md border border-slate-200 p-3">
              <div className="flex items-center gap-2 mb-1">
                <input
                  className="input font-medium"
                  placeholder="Section label (e.g. Put Pricing)"
                  value={s.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                />
                {s.confidence && !s.reviewed && (
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0"
                    title="Claude-extracted, not yet reviewed"
                    aria-label="Unreviewed"
                  />
                )}
                <button
                  className="text-xs text-sky-600 hover:underline whitespace-nowrap shrink-0 disabled:opacity-40"
                  disabled={!s.label.trim()}
                  title="Promote this to a filterable field across all deals"
                  onClick={() => onPromote(s.label)}
                >
                  Promote
                </button>
                <button
                  className="text-xs text-red-600 hover:underline shrink-0"
                  onClick={() => onChange(sections.filter((_, idx) => idx !== i))}
                >
                  ✕
                </button>
              </div>
              <AutoTextarea value={s.value} onChange={(v) => update(i, { value: v })} />
              {!s.reviewed && s.notFoundOnRefresh && (
                <div className="text-[11px] text-amber-700 mt-1">
                  Not found by re-extraction — the newly added documents may have removed this
                  term. Save to keep it, or remove it.
                </div>
              )}
              {!s.reviewed && s.priorReviewedValue !== undefined && (
                <div className="text-[11px] text-amber-700 mt-1">
                  Changed by re-extraction. Previously reviewed:{" "}
                  <span className="font-medium">{s.priorReviewedValue}</span>
                </div>
              )}
              {s.sourceQuote && (
                <p className="text-[11px] text-slate-400 mt-1 italic line-clamp-2" title={s.sourceQuote}>
                  “{s.sourceQuote}”
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldGroup({
  title,
  fields,
  deal,
  draft,
  setDraft,
}: {
  title: string;
  fields: RepoSchemaField[];
  deal: RepositoryDeal;
  draft: Record<string, string>;
  setDraft: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}) {
  if (fields.length === 0) return null;
  return (
    <div className="mt-4 first:mt-0">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
        {title}
      </h3>
      <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        {fields.map((f) => (
          <FieldInput
            key={f.key}
            field={f}
            stored={deal.metadata?.[f.key]}
            value={draft[f.key] ?? ""}
            onChange={(v) => setDraft((prev) => ({ ...prev, [f.key]: v }))}
          />
        ))}
      </div>
    </div>
  );
}

function FieldInput({
  field,
  stored,
  value,
  onChange,
}: {
  field: RepoSchemaField;
  stored: RepoMetadataValue | undefined;
  value: string;
  onChange: (v: string) => void;
}) {
  const isProse = field.group === "prose";
  const wrapClass = isProse ? "sm:col-span-2" : "";

  return (
    <div className={wrapClass}>
      <label className="flex items-center gap-1 text-sm font-medium mb-1" title={field.description}>
        {field.label}
        {stored?.confidence && !stored?.reviewed && (
          <span
            className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400"
            title="Claude-extracted, not yet reviewed"
            aria-label="Unreviewed"
          />
        )}
      </label>
      {stored && !stored.reviewed && stored.priorReviewedValue !== undefined && (
        <div className="text-[11px] text-amber-700 mb-1">
          Changed by re-extraction. Previously reviewed:{" "}
          <span className="font-medium">{formatPriorValue(stored.priorReviewedValue, field)}</span>
        </div>
      )}

      {field.type === "boolean" ? (
        <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      ) : field.type === "enum" ? (
        <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(field.enumValues ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : field.type === "date" ? (
        <input type="date" className="input" value={value} onChange={(e) => onChange(e.target.value)} />
      ) : field.type === "usd" ? (
        <UsdInput value={value} onChange={onChange} />
      ) : field.type === "number" ? (
        field.unit === "%" ? (
          <PctInput value={value} onChange={onChange} />
        ) : field.unit ? (
          <div className="relative">
            <input
              type="number"
              className="input pr-12"
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-400">
              {field.unit}
            </span>
          </div>
        ) : (
          <input type="number" className="input" value={value} onChange={(e) => onChange(e.target.value)} />
        )
      ) : isProse ? (
        <AutoTextarea value={value} onChange={onChange} />
      ) : (
        <input className="input" value={value} onChange={(e) => onChange(e.target.value)} />
      )}

      {stored?.sourceQuote && (
        <p className="text-[11px] text-slate-400 mt-1 italic line-clamp-2" title={stored.sourceQuote}>
          “{stored.sourceQuote}”
        </p>
      )}
    </div>
  );
}

/**
 * Dollar input: shows "$5,480,000" (rounded, thousands-separated) when not
 * focused, and the raw number while editing so it's easy to type into. The
 * draft always holds the plain numeric string.
 */
function UsdInput({
  value,
  onChange,
  placeholder = "$",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [focused, setFocused] = useState(false);
  const n = value === "" ? null : Number(value);
  const display =
    focused
      ? value
      : n != null && Number.isFinite(n)
        ? `$${Math.round(n).toLocaleString()}`
        : "";
  return (
    <input
      className="input"
      inputMode="numeric"
      placeholder={placeholder}
      value={display}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
    />
  );
}

/**
 * Percent input: shows "9%" / "7.12%" (decimals only when the value has them)
 * when not focused, and the raw number while editing. Same draft contract as
 * UsdInput — the draft always holds the plain numeric string.
 */
function PctInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [focused, setFocused] = useState(false);
  const n = value === "" ? null : Number(value);
  const display =
    focused ? value : n != null && Number.isFinite(n) ? `${n}%` : "";
  return (
    <input
      className="input"
      inputMode="decimal"
      placeholder="%"
      value={display}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9.\-]/g, ""))}
    />
  );
}

/**
 * Textarea that grows to fit its content (so the full extracted text is
 * visible without manual dragging) while staying user-resizable.
 */
function AutoTextarea({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const fit = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, 38)}px`;
  };
  useEffect(fit, [value]);
  return (
    <textarea
      ref={ref}
      className="input resize-y"
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onInput={fit}
    />
  );
}

/**
 * Compact custom dropdown for document type: the button truncates with an
 * ellipsis (full label on hover) so it's never cut mid-word, and the open
 * menu is wide enough to show every type in full. Sleeker than a native
 * <select> for a long, growing list.
 */
function DocKindSelect({
  value,
  options,
  label,
  disabled,
  onSelect,
  onAdd,
  onManage,
}: {
  value: string;
  options: { key: string; label: string }[];
  label: string;
  disabled?: boolean;
  onSelect: (key: string) => void;
  onAdd: () => void;
  onManage?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="input py-1 flex items-center justify-between gap-1 text-left disabled:opacity-50"
      >
        <span className="truncate" title={label}>
          {label}
        </span>
        <svg
          className={`h-3 w-3 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-72 max-h-72 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 ${
                o.key === value ? "bg-sky-50 font-medium text-sky-700" : ""
              }`}
              onClick={() => {
                setOpen(false);
                if (o.key !== value) onSelect(o.key);
              }}
            >
              {o.label}
            </button>
          ))}
          <div className="my-1 border-t border-slate-100" />
          <button
            type="button"
            className="block w-full px-3 py-1.5 text-left text-sm text-sky-600 hover:bg-sky-50"
            onClick={() => {
              setOpen(false);
              onAdd();
            }}
          >
            + Add type…
          </button>
          {onManage && (
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-sm text-slate-500 hover:bg-slate-50"
              onClick={() => {
                setOpen(false);
                onManage();
              }}
            >
              Manage types…
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Pull a human message out of an api error (jsonRequest throws "<status> <body>"). */
function apiErrorText(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  const body = raw.replace(/^\d+\s+/, "");
  try {
    const j = JSON.parse(body) as { message?: string };
    if (j && typeof j.message === "string") return j.message;
  } catch {
    /* not JSON — fall through */
  }
  return raw;
}

/**
 * Manage the document-type catalog: rename, toggle whether a type is an
 * amendment (requires a parent link), and delete unused types. This is the
 * only place amendment status is set — keeping it off the quick "+ Add type"
 * path means a misclick can't turn an ordinary type into an amendment.
 */
function TypeManagerModal({
  docKinds,
  onClose,
  onChanged,
}: {
  docKinds: RepoDocKindDef[];
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sorted = [...docKinds].sort((a, b) => {
    if (a.key === "other") return 1;
    if (b.key === "other") return -1;
    return a.label.localeCompare(b.label);
  });

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusyKey(key);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError(apiErrorText(e));
    } finally {
      setBusyKey(null);
    }
  };

  const rename = (k: RepoDocKindDef, label: string) => {
    const next = label.trim();
    if (!next || next === k.label) return;
    void run(k.key, () => api.patchRepoDocKind(k.key, { label: next }));
  };
  const toggleAmendment = (k: RepoDocKindDef, isAmendment: boolean) =>
    void run(k.key, () => api.patchRepoDocKind(k.key, { isAmendment }));
  const remove = (k: RepoDocKindDef) => {
    if (!window.confirm(`Delete the “${k.label}” type? This can't be undone.`)) return;
    void run(k.key, () => api.deleteRepoDocKind(k.key));
  };

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4"
      onMouseDown={onClose}
    >
      <div
        className="card w-full max-w-xl max-h-[80vh] overflow-auto p-4"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold">Document types</h3>
          <button className="text-slate-400 hover:text-slate-600" onClick={onClose}>
            ✕
          </button>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Tick “Amendment” only for types that modify another document (amendment,
          forbearance, …) — those must be linked to the document they amend. Most types
          aren't amendments.
        </p>
        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
        <div className="divide-y divide-slate-100">
          {sorted.map((k) => (
            <div key={k.key} className="flex items-center gap-3 py-2">
              <input
                className="input py-1 flex-1"
                defaultValue={k.label}
                disabled={busyKey === k.key || k.key === "other"}
                onBlur={(e) => rename(k, e.target.value)}
              />
              <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={!!k.isAmendment}
                  disabled={busyKey === k.key || k.key === "other"}
                  onChange={(e) => toggleAmendment(k, e.target.checked)}
                />
                Amendment
              </label>
              <button
                className="text-xs text-red-600 hover:underline disabled:opacity-40"
                disabled={busyKey === k.key || k.key === "other"}
                onClick={() => remove(k)}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Tranches (per-note breakdown)
// ===========================================================================

function TranchesEditor({
  tranches,
  onChange,
}: {
  tranches: RepoTranche[];
  onChange: (t: RepoTranche[]) => void;
}) {
  const update = (i: number, patch: Partial<RepoTranche>) => {
    onChange(tranches.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  };
  const num = (s: string): number | undefined => {
    if (s === "") return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
  };

  return (
    <div className="mb-2">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Notes / Tranches
        </h3>
        <button
          className="text-xs text-sky-600 hover:underline"
          onClick={() =>
            onChange([
              ...tranches,
              { id: `t${Date.now()}${tranches.length}`, label: "" },
            ])
          }
        >
          + Add note
        </button>
      </div>
      {tranches.length === 0 ? (
        <p className="text-xs text-slate-400">
          No notes yet. Add one per note (e.g. “A-1 Note”, “B Note”).
        </p>
      ) : (
        <div className="space-y-2">
          <div className="hidden sm:grid sm:grid-cols-7 gap-2 px-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">
            <span>Label</span>
            <span>Face value $</span>
            <span>Cash funded $</span>
            <span>OID $</span>
            <span>Rate %</span>
            <span>Term (mo)</span>
            <span />
          </div>
          {tranches.map((t, i) => (
            <div key={t.id} className="grid grid-cols-2 sm:grid-cols-7 gap-2 items-center">
              <input
                className="input col-span-2 sm:col-span-1"
                placeholder="Label"
                value={t.label}
                onChange={(e) => update(i, { label: e.target.value })}
              />
              <UsdInput
                placeholder="Face $"
                value={t.faceValueUsd?.toString() ?? ""}
                onChange={(v) => update(i, { faceValueUsd: num(v) })}
              />
              <UsdInput
                placeholder="Funded $"
                value={t.purchasePriceUsd?.toString() ?? ""}
                onChange={(v) => update(i, { purchasePriceUsd: num(v) })}
              />
              <UsdInput
                placeholder="OID $"
                value={t.oidUsd?.toString() ?? ""}
                onChange={(v) => update(i, { oidUsd: num(v) })}
              />
              <input
                className="input"
                type="number"
                placeholder="Rate %"
                value={t.interestRatePct ?? ""}
                onChange={(e) => update(i, { interestRatePct: num(e.target.value) })}
              />
              <input
                className="input"
                type="number"
                placeholder="Term mo"
                value={t.maturityMonths ?? ""}
                onChange={(e) => update(i, { maturityMonths: num(e.target.value) })}
              />
              <div className="flex items-center justify-between gap-1">
                <label className="flex items-center gap-1 text-xs text-slate-500">
                  <input
                    type="checkbox"
                    checked={t.convertible ?? false}
                    onChange={(e) => update(i, { convertible: e.target.checked })}
                  />
                  Conv.
                </label>
                <button
                  className="text-xs text-red-600 hover:underline"
                  onClick={() => onChange(tranches.filter((_, idx) => idx !== i))}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Per-deal chat
// ===========================================================================

function RepoChatPanel({ dealId, preview }: { dealId: string; preview?: boolean }) {
  const [messages, setMessages] = useState<RepoMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  // Progress while the model is silent (loading docs, thinking).
  const [streamStatus, setStreamStatus] = useState<string | null>(null);
  const [thinkingText, setThinkingText] = useState("");
  const [streamStartedAt, setStreamStartedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.listRepoMessages(dealId).then(({ messages }) => setMessages(messages));
    // Pre-warm the prompt cache so the first question doesn't pay full
    // document ingestion. Best-effort — ignore failures.
    api.warmRepoDealChat(dealId).catch(() => {});
  }, [dealId]);

  // Escape exits full screen.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setExpanded(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [expanded]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streamText]);

  const send = async () => {
    const content = input.trim();
    if (!content || streaming) return;
    setInput("");
    setErr(null);
    setStreaming(true);
    setStreamText("");
    setStreamStatus("Sending your message…");
    setThinkingText("");
    setStreamStartedAt(Date.now());
    try {
      await streamRepoMessage(dealId, content, {
        onUserMessage: (m) => setMessages((prev) => [...prev, m]),
        onDelta: (t) => setStreamText((prev) => prev + t),
        onAssistantMessage: (m) => {
          setMessages((prev) => [...prev, m]);
          setStreamText("");
        },
        onStatus: (_phase, detail) => setStreamStatus(detail ?? null),
        onThinking: (t) => setThinkingText((prev) => prev + t),
        onError: (msg) => setErr(msg),
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setStreaming(false);
      setStreamText("");
      setStreamStatus(null);
      setThinkingText("");
      setStreamStartedAt(null);
    }
  };

  return (
    <section className={expanded ? "fixed inset-0 z-40 bg-slate-50 p-4 flex flex-col" : "mb-8"}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-semibold">
          {preview ? "Ask about these documents (preview)" : "Ask about this deal"}
        </h2>
        <div className="flex items-center gap-3">
          {messages.length > 0 && (
            <button
              className="text-xs text-slate-500 hover:text-red-600"
              disabled={streaming}
              onClick={async () => {
                if (!confirm("Clear this deal's conversation for everyone?")) return;
                await api.clearRepoMessages(dealId);
                setMessages([]);
              }}
            >
              Clear conversation
            </button>
          )}
          <button
            className="text-xs text-slate-500 hover:text-ink"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? "Exit full screen (Esc)" : "Expand to full screen"}
          >
            {expanded ? "✕ Exit full screen" : "⤢ Full screen"}
          </button>
        </div>
      </div>

      <div className={`card flex flex-col ${expanded ? "flex-1 min-h-0" : ""}`}>
        <div
          ref={scrollRef}
          className={`overflow-y-auto p-4 space-y-4 ${expanded ? "flex-1" : "max-h-[28rem]"}`}
        >
          {messages.length === 0 && !streaming && (
            <div className="text-center text-sm text-slate-400 py-6">
              Ask anything about this deal's documents — “what's the interest rate?”, “where
              are registration rights?”, “what are the events of default?”
            </div>
          )}
          {messages.map((m) => (
            <ChatBubble key={m.id} message={m} />
          ))}
          {streaming && (
            <div>
              <div className="text-xs text-slate-500 mb-1">Assistant</div>
              {streamText ? (
                <Markdown>{stripCitationsFence(streamText)}</Markdown>
              ) : (
                <StreamProgress
                  label={streamStatus}
                  thinking={thinkingText}
                  startedAt={streamStartedAt}
                  longRunningHint="Reading a large deal takes a few minutes the first time; follow-ups are faster. Keep this tab open."
                />
              )}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 p-3">
          {err && <div className="text-sm text-red-600 mb-2">{err}</div>}
          <div className="flex gap-2 items-end">
            <textarea
              className="input resize-none flex-1"
              rows={2}
              placeholder="Ask a question about this deal…"
              value={input}
              disabled={streaming}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <button className="btn-primary" disabled={streaming || !input.trim()} onClick={send}>
              {streaming ? "…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ChatBubble({ message: m }: { message: RepoMessage }) {
  const isUser = m.role === "user";
  return (
    <div className={isUser ? "text-right" : ""}>
      <div className="text-xs text-slate-500 mb-1">
        {isUser ? m.createdByName ?? m.createdByEmail ?? "You" : "Assistant"}
      </div>
      <div
        className={`inline-block text-left rounded-lg px-3 py-2 ${
          isUser ? "bg-ink text-white" : "bg-slate-50 border border-slate-200 w-full"
        }`}
      >
        {isUser ? (
          <span className="whitespace-pre-wrap text-sm">{m.content}</span>
        ) : (
          <>
            <Markdown>{m.content || (m.error ?? "(empty)")}</Markdown>
            {m.citations && m.citations.length > 0 && <CitationChips citations={m.citations} />}
          </>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Commit
// ===========================================================================

function CommitBar({
  deal,
  docs,
  amendmentKeys,
  onCommitted,
}: {
  deal: RepositoryDeal;
  docs: RepositoryDoc[];
  amendmentKeys: Set<string>;
  onCommitted: (d: RepositoryDeal) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (deal.status === "committed") return null;

  const unlinkedAmendments = docs.filter(
    (d) => amendmentKeys.has(d.docKind) && !d.parentDocId,
  );
  const blockers: string[] = [];
  if (docs.length === 0) blockers.push("Add at least one document.");
  if (unlinkedAmendments.length > 0)
    blockers.push(`Link ${unlinkedAmendments.length} amendment(s) to the document they amend.`);

  return (
    <section className="border-t border-slate-200 pt-4 mt-2">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">
          {blockers.length > 0 ? (
            <ul className="list-disc ml-5">
              {blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          ) : (
            "Ready to commit. Once committed, this deal becomes searchable in the repository."
          )}
        </div>
        <button
          className="btn-primary"
          disabled={busy || blockers.length > 0}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const { deal: updated } = await api.commitRepoDeal(deal.id);
              onCommitted(updated);
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Committing…" : "Commit to repository"}
        </button>
      </div>
      {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
    </section>
  );
}
